/**
 * «Техника» у Экономиста — справочник техники платформы (ТЗ AI-Economist-2026
 * ред. 2.1, п. 6.1, 6.8). Читать справочник может любой, у кого есть доступ к
 * агентам; править, загружать книгу ЭММ и вести группы механизаторов — с
 * правом-действием `fleet.write` (экономисты, главный экономист, admin).
 *
 * Две вкладки: модели техники и группы механизаторов (п. 6.3.1). Вкладка — в
 * адресе (`?tab=groups`), чтобы ссылку можно было переслать.
 */
import { useId } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useListFleetGroupsQuery, useListFleetModelsQuery, useMeQuery } from '@shared/api';
import { ReadOnlyBanner, useActionPermission } from '@entities/user';
import { useEconPermission } from '@entities/economic/types';
import { TabPanel, Tabs, type TabItem } from '@entities/fleet';
import { FleetGroups } from '@widgets/FleetGroups';
import { FleetRegistry } from '@widgets/FleetRegistry';

type FleetTab = 'models' | 'groups';

const TabsBar = styled.div`
  margin: ${({ theme }) => `-${theme.spacing(3)} 0 ${theme.spacing(5)}`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

export function FleetView() {
  const { data: me } = useMeQuery();
  const { can_read, can_write } = useEconPermission();
  const fleetWrite = useActionPermission('fleet.write').allowed;
  const location = useLocation();
  const navigate = useNavigate();
  const uid = useId();
  const tab: FleetTab =
    new URLSearchParams(location.search).get('tab') === 'groups' ? 'groups' : 'models';

  // Счётчики во вкладках — из тех же запросов, что у вкладок (кеш общий).
  const { data: models } = useListFleetModelsQuery({});
  const { data: groups } = useListFleetGroupsQuery({});

  function setTab(next: FleetTab) {
    if (next === tab) return;
    // Карточка модели или группы при смене вкладки закрывается.
    navigate({ search: next === 'groups' ? '?tab=groups' : '' }, { replace: true });
  }

  const tabs: TabItem<FleetTab>[] = [
    // «Модели», а не «Модели техники»: обе вкладки должны помещаться в 360 px.
    { id: 'models', label: 'Модели', count: models?.items.length },
    { id: 'groups', label: 'Группы механизаторов', count: groups?.items.length },
  ];

  return (
    <>
      {/* Читателям Экономиста плашку уже показала шапка раздела (EconLayout);
          здесь — тем, кто пришёл без доступа к Экономисту. */}
      {me && !can_read && !can_write && <ReadOnlyBanner />}
      <TabsBar>
        <Tabs
          items={tabs}
          value={tab}
          onChange={setTab}
          idPrefix={uid}
          label="Справочник техники"
        />
      </TabsBar>
      <TabPanel idPrefix={uid} id={tab}>
        {tab === 'groups' ? (
          <FleetGroups canWrite={fleetWrite} canReadRates={can_read || can_write} />
        ) : (
          <FleetRegistry canWrite={fleetWrite} />
        )}
      </TabPanel>
    </>
  );
}
